import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import SearchButtonInput from "@/components/ui/search-button-input";
import { EmptyFilters, Filters, FiltersSetter } from "@/services/api/types";
import { ViewDataConfiguration, ViewType } from "@/entities/views/types";
import { SortingState, Updater } from "@tanstack/react-table";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import ToolbarFilterButton from "@/components/controls/ToolbarActions/ToolbarFilterButton/ToolbarFilterButton";
import FilterChip from "@/components/controls/Filters/FilterChip";
import { ColumnData } from "@/entities/dataTables/types";
import { APIFieldItem } from "@/pages/settings/types";
import { forEach, isEqual, keys } from "lodash";
import DisplayButton from "@/components/controls/ToolbarActions/DisplayButton";
import type { CompareToSelection } from "@/entities/dataTables/compareTo/types";
import { Button } from "@/components/ui/button";
import {
  applyTempViewsToServerViews,
  deleteTempView,
  haveColumnsChangedFromSaved,
  mergeMissingDefaultColumns,
  recordActivatedViewConfig,
  saveConfigToTempView,
  saveSelectedViewId,
  clearSelectedViewId
} from "@/entities/dataTables/helpers";
import {
  hasActiveFilterValue,
  markViewAppliedFilters
} from "@/components/controls/ToolbarActions/utils";
import { useRevealFilteredColumns } from "@/components/controls/ToolbarActions/useRevealFilteredColumns";
import NewViewActions from "@/components/controls/ToolbarActions/NewViewActions";
import { useUpdateViewsMutation, useViewsAll } from "@/entities/views/hooks";
import {
  normalizeFiltersAfterDeserialization,
  sortQueryToState,
  resolveCurrentUserFilters
} from "@/entities/views/helpers";
import { useUsersMe } from "@/entities/users/hooks";
import toast from "react-hot-toast";
import { Close } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { getAllFilterOptions, getFilterLabel } from "@/hooks/useFilterOptions";

const EMPTY_COLUMNS_STATE: ColumnData[] = [];

const TRANSLATION_KEY = "datatoolbar";

interface Props {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filters: Filters;
  setFilters: FiltersSetter;
  filterOptions: DataTableFilter[];
  groupBy?: string[];
  setGroupBy?: (groupBy: string[]) => void;
  groupByOptions?: string[];
  sorting?: SortingState;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  sortByOptions?: string[];
  defaultSortByValue?: string;
  columnsState?: ColumnData[];
  setColumnsState?: React.Dispatch<React.SetStateAction<ColumnData[]>>;
  defaultColumnsState?: ColumnData[];
  // Off for tables whose column list does not start with the table's locked entity column.
  lockFirstColumn?: boolean;
  selectedView?: ViewDataConfiguration;
  setSelectedView?: React.Dispatch<React.SetStateAction<ViewDataConfiguration>>;
  viewType?: ViewType;
  shouldShowEditColumns?: boolean;
  showTableColumns?: boolean;
  showDisplayButton?: boolean;
  showSearch?: boolean;
  showFilters?: boolean;
  showAsValue?: string;
  onShowAsChange?: (value: string) => void;
  showAsOptions?: { value: string; label: React.ReactNode }[];
  // Compare-to. Omitted by every table that does not offer it, which
  // is what keeps the Display menu unchanged for them.
  compareTo?: CompareToSelection;
  onCompareToChange?: (selection: CompareToSelection) => void;
  defaultCompareTo?: CompareToSelection;
  sortMenuExcludedColumnIds?: ReadonlySet<string>;
  // Filter keys left out of the add-filter menu. Saved filters on them still
  // resolve and render their chips.
  filterPickerExcludedKeys?: string[];
  // Offers date columns by month, quarter and year; see DisplayPopover.
  groupByDateBuckets?: boolean;
  onColumnsChange?: (updatedColumns: ColumnData[]) => void;
  customFields?: APIFieldItem[];
  groupByFields?: APIFieldItem[];
  tableId?: string;
  className?: string;
  actionsClassName?: string;
  // Base filters that should not be clearable or shown as chips
  baseFilters?: Filters;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
}

const ToolbarActions: React.FC<Props> = ({
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  filterOptions,
  groupBy = [],
  setGroupBy,
  groupByOptions = [],
  sorting,
  onSortingChange,
  sortByOptions = [],
  defaultSortByValue = "",
  columnsState,
  setColumnsState,
  defaultColumnsState = EMPTY_COLUMNS_STATE,
  lockFirstColumn = true,
  selectedView,
  setSelectedView,
  viewType,
  showTableColumns = true,
  showDisplayButton = true,
  showSearch = true,
  showFilters = true,
  showAsValue,
  onShowAsChange,
  showAsOptions,
  compareTo,
  onCompareToChange,
  defaultCompareTo,
  sortMenuExcludedColumnIds,
  filterPickerExcludedKeys,
  groupByDateBuckets,
  onColumnsChange,
  customFields,
  groupByFields,
  tableId = "",
  className,
  actionsClassName,
  baseFilters = EmptyFilters,
  leftActions,
  rightActions
}) => {
  const { t } = useTranslation();
  const [internalViews, setInternalViews] = useState<ViewDataConfiguration[]>(
    []
  );

  const { views: loadedViews } = useViewsAll(viewType ?? ViewType.DEALS_TABLE);
  const currentUserId = useUsersMe().data?.id;

  const activateView = useCallback(
    (newView: ViewDataConfiguration) => {
      if (newView && setSelectedView) {
        newView.isOpen = true;
        recordActivatedViewConfig(newView);
        setSelectedView(newView);
        if (newView.id && viewType) {
          saveSelectedViewId(viewType, newView.id);
        }

        const normalizedFilters = normalizeFiltersAfterDeserialization(
          resolveCurrentUserFilters(
            newView.config?.filters ?? EmptyFilters,
            currentUserId
          )
        );
        setFilters(markViewAppliedFilters(normalizedFilters));

        if (onSortingChange) {
          onSortingChange(
            sortQueryToState(newView.config?.sort, defaultSortByValue)
          );
        }
        setSearchTerm(newView.config?.searchTerm || "");
        if (setGroupBy) {
          setGroupBy(newView.config?.groupBy ?? []);
        }
        if (setColumnsState) {
          setColumnsState(newView.config?.columns ?? []);
        }
      }
    },
    [
      setSelectedView,
      setFilters,
      onSortingChange,
      defaultSortByValue,
      setSearchTerm,
      setGroupBy,
      setColumnsState,
      viewType,
      currentUserId
    ]
  );

  const { mutate: updateViews } = useUpdateViewsMutation();
  const setAndSaveViews = useCallback(
    async (
      newViews: ViewDataConfiguration[],
      oldViews: ViewDataConfiguration[]
    ) => {
      setInternalViews(newViews);

      const removedViews = oldViews.filter(
        (oldView) => !newViews.some((newView) => newView.id === oldView.id)
      );

      forEach(removedViews, (removedView) => {
        deleteTempView(removedView);
        if (viewType && removedView.id === selectedView?.id) {
          clearSelectedViewId(viewType);
        }
      });

      if (!viewType) return;

      const result = await updateViews({
        views: newViews,
        previousViews: oldViews,
        type: viewType
      });

      if (result.error) {
        toast.error(
          <ToastBody
            title={t("datatoolbar.views.errorSaving")}
            actions={
              <IconButton
                size="md"
                variant="iconNoBg"
                iconColor="secondary"
                onClick={() => toast.dismiss()}
              >
                <Close />
              </IconButton>
            }
          />,
          {
            icon: null
          }
        );
      }
    },
    [viewType, updateViews, t]
  );

  useEffect(() => {
    if (viewType && loadedViews?.length > 0) {
      const tempViews = applyTempViewsToServerViews(loadedViews);
      const typeViews = tempViews.filter((view) => view.type === viewType);
      setInternalViews(typeViews);
    }
  }, [loadedViews, viewType]);

  const baseFilterKeys = useMemo(() => keys(baseFilters), [baseFilters]);

  const allActiveFilters = useMemo(() => {
    return keys(filters).filter((key) => {
      const value = filters[key as keyof Filters];
      return hasActiveFilterValue(value);
    });
  }, [filters]);

  const activeFilters = useMemo(() => {
    return allActiveFilters.filter((key) => !baseFilterKeys.includes(key));
  }, [allActiveFilters, baseFilterKeys]);

  const hasFiltersChanged = useMemo(() => {
    if (!selectedView?.id) {
      return activeFilters.length > 0;
    }

    const originalView = loadedViews?.find((v) => v.id === selectedView.id);
    const viewDefaultFilters = originalView?.config?.filters ?? EmptyFilters;

    const normalizedViewFilters =
      normalizeFiltersAfterDeserialization(viewDefaultFilters);

    return !isEqual(filters, normalizedViewFilters);
  }, [filters, selectedView?.id, loadedViews, activeFilters.length]);

  const hasSortingChanged = useMemo(() => {
    if (!selectedView?.id || !sorting) {
      return false;
    }

    const originalView = loadedViews?.find((v) => v.id === selectedView.id);
    const viewDefaultSorting = originalView?.config?.sort
      ? sortQueryToState(originalView.config.sort)
      : [];

    return !isEqual(sorting, viewDefaultSorting);
  }, [sorting, selectedView?.id, loadedViews]);

  // The columns the selected view was saved with. When the view has no saved
  // columns yet, fall back to code-defined defaults.
  const savedViewColumns = useMemo(() => {
    const originalView = loadedViews?.find(
      (view) => view.id === selectedView?.id
    );
    const viewSavedColumns = originalView?.config?.columns;
    return viewSavedColumns?.length ? viewSavedColumns : defaultColumnsState;
  }, [selectedView?.id, loadedViews, defaultColumnsState]);

  const hasColumnsChanged = useMemo(() => {
    if (!selectedView?.id || !columnsState || columnsState.length === 0) {
      return false;
    }

    if (!savedViewColumns.length) {
      return columnsState.some((col) => col.isVisible === false);
    }

    return haveColumnsChangedFromSaved(columnsState, savedViewColumns);
  }, [columnsState, selectedView?.id, savedViewColumns]);

  const hasViewChanged =
    hasFiltersChanged || hasSortingChanged || hasColumnsChanged;

  const isNonSystemViewWithChanges = useMemo(() => {
    if (!selectedView?.id || selectedView?.isSystem) {
      return false;
    }
    return hasViewChanged;
  }, [selectedView?.id, selectedView?.isSystem, hasViewChanged]);

  const resetFiltersToViewDefault = useCallback(() => {
    if (!selectedView?.id) return;

    const originalView = loadedViews?.find((v) => v.id === selectedView.id);
    const viewDefaultFilters = originalView?.config?.filters ?? EmptyFilters;
    const normalizedViewFilters =
      normalizeFiltersAfterDeserialization(viewDefaultFilters);

    setFilters(markViewAppliedFilters(normalizedViewFilters));

    if (onSortingChange && originalView?.config?.sort) {
      const viewDefaultSorting = sortQueryToState(originalView.config.sort);
      onSortingChange(viewDefaultSorting);
    }

    // A filter the user added may have revealed a column the view hides; the
    // session draft would otherwise keep it visible across reloads. Keep the
    // table's current labels and the columns it gained since the view was
    // saved, as loading the view does.
    if (setColumnsState && savedViewColumns.length) {
      setColumnsState((previousColumns) => {
        const labelById = new Map(
          previousColumns.map((column) => [column.id, column.label])
        );
        return mergeMissingDefaultColumns(
          savedViewColumns.map((column) => ({
            ...column,
            label: labelById.get(column.id) ?? column.label
          })),
          defaultColumnsState
        );
      });
    }
  }, [
    selectedView?.id,
    loadedViews,
    setFilters,
    onSortingChange,
    setColumnsState,
    savedViewColumns,
    defaultColumnsState
  ]);

  // Use defaultColumnsState (not columnsState) so filter options include all schema fields
  // regardless of the user's current column visibility/order.
  const allFilterOptions = useMemo(
    () => getAllFilterOptions(filterOptions, defaultColumnsState, customFields),
    [filterOptions, defaultColumnsState, customFields]
  );

  const getFilterOption = (filterKey: string): DataTableFilter | undefined => {
    return allFilterOptions.find((opt) => opt.key === filterKey);
  };

  const removeFilter = (filterKey: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[filterKey as keyof Filters];
      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setFilters(baseFilters);
  };

  useEffect(() => {
    if (selectedView) {
      const config = {
        filters,
        sort:
          sorting && sorting.length > 0
            ? { field: sorting[0].id, ascending: !sorting[0].desc }
            : undefined,
        groupBy,
        searchTerm,
        columns: columnsState
      };

      saveConfigToTempView(selectedView, config);
    }
  }, [filters, sorting, groupBy, searchTerm, selectedView, columnsState]);

  useRevealFilteredColumns(
    filters,
    columnsState,
    setColumnsState,
    defaultColumnsState
  );

  return (
    <div
      data-test="toolbar-actions"
      className={cn(
        "flex gap-2 border-b border-border-border1 justify-between",
        className
      )}
    >
      <div
        data-test="toolbar-actions-content"
        className={cn(
          "flex items-center w-full justify-between",
          actionsClassName
        )}
      >
        <div
          data-test="toolbar-actions-left"
          className="flex items-center gap-[6px] flex-wrap"
        >
          {leftActions}
          {showSearch && (
            <SearchButtonInput
              placeholder={t("search")}
              data-test="toolbar-actions-search"
              inputValue={searchTerm}
              setInputValue={setSearchTerm}
              searchIconColor="secondary"
              tooltipText={t("search")}
              iconButtonSize="lg"
            />
          )}
          {showFilters && (
            <ToolbarFilterButton
              data-test="toolbar-actions-filter-button"
              allFilterOptions={allFilterOptions}
              ignoredColumns={filterPickerExcludedKeys}
              filters={filters}
              setFilters={setFilters}
              hasActiveFilters={activeFilters.length > 0}
            />
          )}
          {showFilters &&
            activeFilters.map((filterKey) => {
            const filterOption = getFilterOption(filterKey);
            if (!filterOption) return null;
            const isMonetary =
              filterOption.filterType.type === "numeric" &&
              filterOption.filterType.isMonetary === true;
            const isPercentage =
              filterOption.filterType.type === "numeric" &&
              filterOption.filterType.isPercentage === true;
            const filterLabel = getFilterLabel(filterOption);
            return (
              <FilterChip
                data-test={`toolbar-actions-filter-chip-${filterKey}`}
                key={filterKey}
                filterKey={filterKey}
                filterLabel={filterLabel}
                filterOption={filterOption}
                filters={filters}
                setFilters={setFilters}
                onRemove={() => removeFilter(filterKey)}
                isMonetary={isMonetary}
                isPercentage={isPercentage}
              />
            );
          })}
        </div>
        <div
          data-test="toolbar-actions-right"
          className="flex items-center gap-2 self-start"
        >
          {showFilters && hasFiltersChanged && (
            <Button
              data-test="toolbar-actions-clear-filters-button"
              variant="tertiary"
              className="!py-[3.5px]"
              onClick={
                isNonSystemViewWithChanges
                  ? resetFiltersToViewDefault
                  : clearAllFilters
              }
            >
              {isNonSystemViewWithChanges
                ? t(`${TRANSLATION_KEY}.filters.resetView`)
                : t(`${TRANSLATION_KEY}.filters.clear`)}
            </Button>
          )}
          {hasViewChanged &&
            selectedView &&
            internalViews.length > 0 &&
            viewType && (
              <NewViewActions
                selectedView={selectedView}
                views={internalViews}
                setAndSaveViews={setAndSaveViews}
                filters={filters}
                sorting={sorting}
                defaultSortByValue={defaultSortByValue}
                groupBy={groupBy}
                searchTerm={searchTerm}
                columnsState={columnsState ?? []}
                viewType={viewType}
                activateView={activateView}
              />
            )}
          {showDisplayButton && (
            <DisplayButton
              data-test="toolbar-actions-display-button"
              groupBy={groupBy}
              setGroupBy={setGroupBy}
              groupByOptions={groupByOptions}
              groupByFilterOptions={allFilterOptions}
              sorting={sorting}
              onSortingChange={onSortingChange}
              sortByOptions={sortByOptions}
              defaultSortField={defaultSortByValue}
              selectedView={selectedView}
              columnsState={columnsState}
              defaultColumnsState={defaultColumnsState}
              lockFirstColumn={lockFirstColumn}
              onColumnsApply={onColumnsChange || setColumnsState}
              tableId={tableId}
              customFields={customFields}
              groupByFields={groupByFields}
              showTableColumns={showTableColumns}
              showAsValue={showAsValue}
              onShowAsChange={onShowAsChange}
              showAsOptions={showAsOptions}
              compareTo={compareTo}
              onCompareToChange={onCompareToChange}
              defaultCompareTo={defaultCompareTo}
              sortMenuExcludedColumnIds={sortMenuExcludedColumnIds}
              groupByDateBuckets={groupByDateBuckets}
            />
          )}
          {rightActions}
        </div>
      </div>
    </div>
  );
};

export default ToolbarActions;
